/**
 * 계산서에 실을 **실제 계산식**을 만든다.
 *
 * 식 번호("식 (9)")만 적으면 계산서 구실을 못 한다. 검토자가 확인해야 하는 것은
 *   ① 어떤 식을 썼는가 (기호식)
 *   ② 어떤 값을 대입했는가 (대입식)
 *   ③ 그래서 얼마가 나왔는가 (결과)
 * 이 셋이다. 손으로 쓴 계산서가 늘 이 형태인 이유이기도 하다.
 *
 * 수식 라이브러리(KaTeX 등)를 쓰지 않는다. 파일이 몇 배로 커져 단일 HTML
 * 배포가 곤란해지고, 인쇄 품질도 유니코드 기호 쪽이 더 안정적이다.
 * √ · × ÷ 같은 기호와 괄호만으로 충분히 읽힌다.
 */
import type { SizingDetail } from "../calc/governingCase";
import { ATMOSPHERIC_KPA } from "./pressureUnit";

export interface FormulaBlock {
  /** 구하는 값의 이름. 예: "소요 유효 오리피스 면적 A" */
  readonly label: string;
  /** 근거 식 번호·조항 */
  readonly source: string;
  /** 기호로 쓴 식 */
  readonly symbolic: string;
  /** 숫자를 대입한 식. 대입이 무의미하면 null(예: 음함수 수치해) */
  readonly substituted: string | null;
  /** 결과값 + 단위 */
  readonly result: string;
  /** 필요할 때만 덧붙이는 한 줄 설명 */
  readonly note?: string;
}

/** 유효숫자 위주로 읽기 좋게. 아주 크거나 작은 값은 지수로. */
function n(v: number, digits = 4): string {
  if (!Number.isFinite(v)) return "—";
  if (v !== 0 && (Math.abs(v) >= 1e7 || Math.abs(v) < 1e-4)) {
    return v.toExponential(3);
  }
  const abs = Math.abs(v);
  const d = abs >= 1000 ? Math.min(digits, 1) : abs >= 100 ? Math.min(digits, 2) : digits;
  return v
    .toLocaleString("ko-KR", { maximumFractionDigits: d })
    .replace(/(\.\d*?)0+$/, "$1")
    .replace(/\.$/, "");
}

/** 정수처럼 보이게 (유량 등) */
function ni(v: number): string {
  return Number.isFinite(v) ? v.toLocaleString("ko-KR", { maximumFractionDigits: 0 }) : "—";
}

/** 사이징 결과에서 계산 과정을 만든다. */
export function sizingFormulas(detail: SizingDetail): FormulaBlock[] {
  switch (detail.kind) {
    case "gas":
      return gasFormulas(detail);
    case "steam":
      return steamFormulas(detail);
    case "liquid":
      return liquidFormulas(detail);
    case "two-phase":
      return twoPhaseFormulas(detail);
    case "subcooled":
      return subcooledFormulas(detail);
  }
}

/* ── 기체·증기 (5.6) ─────────────────────────────────────── */

function gasFormulas(
  d: Extract<SizingDetail, { kind: "gas" }>,
): FormulaBlock[] {
  const { input: i, result: r } = d;
  const m = r.intermediates;
  const k = i.specificHeatRatio;
  const out: FormulaBlock[] = [];

  out.push({
    label: "임계유동 압력비 Pcf/P1",
    source: "API 520 Part I 식 (5)",
    symbolic: "Pcf / P1 = ( 2 / (k + 1) ) ^ ( k / (k − 1) )",
    substituted: `= ( 2 / (${n(k)} + 1) ) ^ ( ${n(k)} / (${n(k)} − 1) ) = ( ${n(2 / (k + 1), 6)} ) ^ ${n(k / (k - 1), 4)}`,
    result: n(m.criticalPressureRatio, 4),
    note: `Pcf = ${n(m.criticalPressureRatio, 4)} × ${n(i.relievingPressureKpaAbs, 1)} = ${n(m.criticalFlowPressureKpaAbs, 1)} kPa abs · P2 = ${n(i.backpressureKpaAbs, 1)} kPa abs → ${r.flowRegime === "critical" ? "임계유동" : "아임계유동"}`,
  });

  if (m.coefficientC !== null) {
    out.push({
      label: "비열비 함수 C",
      source: "API 520 Part I 식 (13) — SI",
      symbolic: "C = 0.03948 × √( k × ( 2 / (k + 1) ) ^ ( (k + 1) / (k − 1) ) )",
      substituted: `= 0.03948 × √( ${n(k)} × ( 2 / ${n(k + 1)} ) ^ ( ${n(k + 1)} / ${n(k - 1)} ) )`,
      result: n(m.coefficientC, 5),
    });
    out.push({
      label: "소요 유효 오리피스 면적 A",
      source: "API 520 Part I 식 (9) — SI, 임계유동",
      symbolic: "A = W / ( C × Kd × P1 × Kb × Kc ) × √( T × Z / M )",
      substituted:
        `= ${ni(i.requiredFlowKgH)} / ( ${n(m.coefficientC, 5)} × ${n(m.kd, 3)} × ${n(i.relievingPressureKpaAbs, 1)} × ${n(m.kb, 3)} × ${n(m.kc, 3)} )` +
        ` × √( ${n(i.relievingTemperatureK, 1)} × ${n(i.compressibility, 3)} / ${n(i.molecularWeight, 2)} )`,
      result: `${ni(r.requiredAreaMm2)} mm²`,
    });
    return out;
  }

  const r2 = m.backpressureRatio;
  out.push({
    label: "아임계 유동계수 F2",
    source: "API 520 Part I 식 (22)",
    symbolic:
      "F2 = √( k / (k − 1) × r ^ (2/k) × ( 1 − r ^ ((k−1)/k) ) / ( 1 − r ) )   ,  r = P2 / P1",
    substituted:
      `r = ${n(i.backpressureKpaAbs, 1)} / ${n(i.relievingPressureKpaAbs, 1)} = ${n(r2, 4)}` +
      `\n= √( ${n(k)} / ${n(k - 1)} × ${n(r2, 4)} ^ ${n(2 / k, 4)} × ( 1 − ${n(r2, 4)} ^ ${n((k - 1) / k, 4)} ) / ( 1 − ${n(r2, 4)} ) )`,
    result: n(m.coefficientF2 ?? Number.NaN, 4),
  });
  out.push({
    label: "소요 유효 오리피스 면적 A",
    source: "API 520 Part I 식 (19) — SI, 아임계유동",
    symbolic: "A = 17.9 × W / ( F2 × Kd × Kc ) × √( T × Z / ( M × P1 × (P1 − P2) ) )",
    substituted:
      `= 17.9 × ${ni(i.requiredFlowKgH)} / ( ${n(m.coefficientF2 ?? Number.NaN, 4)} × ${n(m.kd, 3)} × ${n(m.kc, 3)} )` +
      ` × √( ${n(i.relievingTemperatureK, 1)} × ${n(i.compressibility, 3)} / ( ${n(i.molecularWeight, 2)} × ${n(i.relievingPressureKpaAbs, 1)} × ${n(i.relievingPressureKpaAbs - i.backpressureKpaAbs, 1)} ) )`,
    result: `${ni(r.requiredAreaMm2)} mm²`,
  });
  return out;
}

/* ── 수증기 (5.7) ────────────────────────────────────────── */

function steamFormulas(
  d: Extract<SizingDetail, { kind: "steam" }>,
): FormulaBlock[] {
  const { input: i, result: r } = d;
  const m = r.intermediates;
  const P1 = i.relievingPressureKpaAbs;
  const out: FormulaBlock[] = [];

  out.push(
    m.knEquation === "API 520 Part I (27)"
      ? {
          label: "Napier 보정계수 KN",
          source: "API 520 Part I 식 (27)",
          symbolic: "KN = 1.0        (P1 ≤ 10,339 kPa)",
          substituted: `P1 = ${n(P1, 1)} kPa ≤ 10,339 kPa`,
          result: "1.0",
        }
      : {
          label: "Napier 보정계수 KN",
          source: "API 520 Part I 식 (29) — SI",
          symbolic: "KN = ( 0.02764 × P1 − 1000 ) / ( 0.03324 × P1 − 1061 )",
          substituted: `= ( 0.02764 × ${n(P1, 1)} − 1000 ) / ( 0.03324 × ${n(P1, 1)} − 1061 ) = ( ${n(0.02764 * P1 - 1000, 2)} ) / ( ${n(0.03324 * P1 - 1061, 2)} )`,
          result: n(m.kn, 4),
        },
  );

  out.push({
    label: "과열 보정계수 KSH",
    source:
      m.kshSource === "saturated"
        ? "API 520 Part I 5.7.1 — 포화증기"
        : m.kshSource === "override"
          ? "직접 지정"
          : "API 520 Part I Table 13",
    symbolic:
      m.kshSource === "saturated"
        ? "KSH = 1.0        (포화증기)"
        : "KSH = f( P1 , T )        Table 13 에서 압력·온도 양방향 선형보간",
    substituted:
      m.kshSource === "table-13"
        ? `P1 = ${n(P1 / 1000, 4)} MPa , T = ${n(i.relievingTemperatureC ?? Number.NaN, 1)} ℃${m.kshInterpolated ? " (보간)" : " (격자점)"}`
        : null,
    result: n(m.ksh, 4),
  });

  out.push({
    label: "소요 유효 오리피스 면적 A",
    source: "API 520 Part I 식 (26) — SI",
    symbolic: "A = 190.5 × W / ( P1 × Kd × Kb × Kc × KN × KSH )",
    substituted: `= 190.5 × ${ni(i.requiredFlowKgH)} / ( ${n(P1, 1)} × ${n(m.kd, 3)} × ${n(m.kb, 3)} × ${n(m.kc, 3)} × ${n(m.kn, 4)} × ${n(m.ksh, 4)} )`,
    result: `${ni(r.requiredAreaMm2)} mm²`,
  });
  return out;
}

/* ── 액체 (5.8 / 5.9) ────────────────────────────────────── */

function liquidFormulas(
  d: Extract<SizingDetail, { kind: "liquid" }>,
): FormulaBlock[] {
  const { input: i, result: r } = d;
  const m = r.intermediates;
  const nonCert = r.equation === "API 520 Part I (43)";
  const out: FormulaBlock[] = [];

  const kpTerm = m.kp !== null ? ` × ${n(m.kp, 3)}` : "";
  const kpSym = m.kp !== null ? " × Kp" : "";
  const dpSym = nonCert ? "1.25 × Ps − P2" : "P1 − P2";

  out.push({
    label: nonCert
      ? "점도보정 전 소요 면적 AR"
      : "점도보정 전 소요 면적 AR",
    source: nonCert
      ? "API 520 Part I 식 (43) — SI, 비인증 밸브"
      : "API 520 Part I 식 (33) — SI, 인증 밸브",
    symbolic: `AR = 11.78 × Q / ( Kd × Kw × Kc${kpSym} ) × √( Gl / ( ${dpSym} ) )`,
    substituted:
      `= 11.78 × ${ni(i.requiredFlowLpm)} / ( ${n(m.kd, 3)} × ${n(m.kw, 3)} × ${n(m.kc, 3)}${kpTerm} )` +
      ` × √( ${n(i.specificGravity, 4)} / ${n(m.differentialPressureKpa, 1)} )`,
    result: `${ni(m.uncorrectedAreaMm2)} mm²`,
    note: `차압 ${dpSym} = ${n(m.differentialPressureKpa, 1)} kPa (게이지압 기준)`,
  });

  if (m.reynolds !== null && m.selectedOrificeForReynolds !== null) {
    const A = m.selectedOrificeForReynolds.effectiveAreaMm2;
    const v = i.viscosity!;
    out.push(
      v.kind === "cP"
        ? {
            label: "레이놀즈수 Re",
            source: "API 520 Part I 식 (37) — SI, 절대점도 기준",
            symbolic: "Re = Q × ( 18,800 × Gl ) / ( μ × √A_selected )",
            substituted: `= ${ni(i.requiredFlowLpm)} × ( 18,800 × ${n(i.specificGravity, 4)} ) / ( ${n(v.value, 1)} × √${ni(A)} )`,
            result: ni(m.reynolds),
            note: `A_selected = ${m.selectedOrificeForReynolds.designation} 오리피스 ${ni(A)} mm² (AR 이상인 최소 표준 규격)`,
          }
        : {
            label: "레이놀즈수 Re",
            source: "API 520 Part I 식 (38) — SI, Saybolt 기준",
            symbolic: "Re = 85,220 × Q / ( U × √A_selected )",
            substituted: `= 85,220 × ${ni(i.requiredFlowLpm)} / ( ${ni(v.value)} × √${ni(A)} )`,
            result: ni(m.reynolds),
            note: `A_selected = ${m.selectedOrificeForReynolds.designation} 오리피스 ${ni(A)} mm² (AR 이상인 최소 표준 규격)`,
          },
    );
    out.push({
      label: "점도 보정계수 Kv",
      source: "API 520 Part I 식 (34)",
      symbolic: "Kv = ( 170 / Re + 1 ) ^ ( −0.5 )",
      substituted: `= ( 170 / ${ni(m.reynolds)} + 1 ) ^ ( −0.5 ) = ( ${n(170 / m.reynolds + 1, 5)} ) ^ ( −0.5 )`,
      result: n(m.kv, 4),
    });
    out.push({
      label: "소요 유효 오리피스 면적 A",
      source: "API 520 Part I 5.8.1.5",
      symbolic: "A = AR / Kv",
      substituted: `= ${ni(m.uncorrectedAreaMm2)} / ${n(m.kv, 4)}`,
      result: `${ni(r.requiredAreaMm2)} mm²`,
      note:
        m.viscosityIterations > 1
          ? `보정 결과가 처음 고른 오리피스를 넘어 ${m.viscosityIterations}회 반복해 확정했다.`
          : "보정 후에도 선정 오리피스 안에 들어와 1회로 확정.",
    });
  } else {
    out.push({
      label: "소요 유효 오리피스 면적 A",
      source: "점도보정 없음 (Kv = 1.0)",
      symbolic: "A = AR",
      substituted: null,
      result: `${ni(r.requiredAreaMm2)} mm²`,
    });
  }
  return out;
}

/* ── 2상 유동, 입구 2상 (Annex C.2.2) ────────────────────── */

function twoPhaseFormulas(
  d: Extract<SizingDetail, { kind: "two-phase" }>,
): FormulaBlock[] {
  const { input: i, result: r } = d;
  const m = r.intermediates;
  const P1pa = i.relievingPressureKpaAbs * 1000;
  const out: FormulaBlock[] = [];

  out.push({
    label: "오메가 파라미터 ω",
    source: "API 520 Part I 식 (C.12)",
    symbolic: "ω = 9 × ( v9 / v1 − 1 )",
    substituted: `= 9 × ( ${n(i.flashSpecificVolumeM3Kg, 6)} / ${n(i.inletSpecificVolumeM3Kg, 6)} − 1 )`,
    result: n(m.omega, 4),
    note: `v9 는 P1 의 90 % (${n(i.relievingPressureKpaAbs * 0.9, 1)} kPa) 에서 플래시한 비체적`,
  });

  out.push({
    label: "임계 압력비 ηc",
    source:
      m.criticalRatioEquation === "API 520 Part I (C.14)"
        ? "API 520 Part I 식 (C.14) — 정의식, 수치해"
        : "API 520 Part I 식 (C.15) — 근사식",
    symbolic:
      m.criticalRatioEquation === "API 520 Part I (C.14)"
        ? "ηc² + ( ω² − 2ω )( 1 − ηc )² + 2ω² × ln ηc + 2ω² ( 1 − ηc ) = 0"
        : "ηc = [ 1 + ( 1.0446 − 0.0093431 × ω^0.5 ) × ω^(−0.56261) ] ^ ( −0.70356 + 0.014685 × ln ω )",
    substituted:
      m.criticalRatioEquation === "API 520 Part I (C.14)"
        ? `ω = ${n(m.omega, 4)} 를 넣고 (0, 1) 구간에서 이분법으로 푼다`
        : `= [ 1 + ( 1.0446 − 0.0093431 × ${n(m.omega, 4)}^0.5 ) × ${n(m.omega, 4)}^(−0.56261) ] ^ ( −0.70356 + 0.014685 × ln ${n(m.omega, 4)} )`,
    result: n(m.criticalPressureRatio, 4),
    note: `Pcf = ${n(m.criticalPressureRatio, 4)} × ${n(i.relievingPressureKpaAbs, 1)} = ${n(m.criticalPressureKpaAbs, 1)} kPa abs · P2 = ${n(i.backpressureKpaAbs, 1)} kPa abs → ${r.flowRegime === "critical" ? "임계유동" : "아임계유동"}`,
  });

  out.push(
    r.flowRegime === "critical"
      ? {
          label: "질량유속 G",
          source: "API 520 Part I 식 (C.18) — SI, 임계유동",
          symbolic: "G = ηc × √( P1 / ( v1 × ω ) )        (P1: Pa)",
          substituted: `= ${n(m.criticalPressureRatio, 4)} × √( ${ni(P1pa)} / ( ${n(i.inletSpecificVolumeM3Kg, 6)} × ${n(m.omega, 4)} ) )`,
          result: `${ni(m.massFlux)} kg/s·m²`,
        }
      : {
          label: "질량유속 G",
          source: "API 520 Part I 식 (C.19) — SI, 아임계유동",
          symbolic:
            "G = √( −2 [ ω × ln ηa + ( ω − 1 )( 1 − ηa ) ] ) / ( ω ( 1/ηa − 1 ) + 1 ) × √( P1 / v1 )",
          substituted: `ηa = ${n(m.backpressureRatio, 4)} , ω = ${n(m.omega, 4)} , P1 = ${ni(P1pa)} Pa , v1 = ${n(i.inletSpecificVolumeM3Kg, 6)} m³/kg`,
          result: `${ni(m.massFlux)} kg/s·m²`,
        },
  );

  out.push({
    label: "소요 유효 오리피스 면적 A",
    source: "API 520 Part I 식 (C.21) — SI",
    symbolic: "A = 277.8 × W / ( Kd × Kb × Kc × Kv × G )",
    substituted: `= 277.8 × ${ni(i.requiredFlowKgH)} / ( ${n(m.kd, 3)} × ${n(m.kb, 3)} × ${n(m.kc, 3)} × ${n(m.kv, 3)} × ${ni(m.massFlux)} )`,
    result: `${ni(r.requiredAreaMm2)} mm²`,
  });
  return out;
}

/* ── 2상 유동, 입구 과냉 액체 (Annex C.2.3) ──────────────── */

function subcooledFormulas(
  d: Extract<SizingDetail, { kind: "subcooled" }>,
): FormulaBlock[] {
  const { input: i, result: r } = d;
  const m = r.intermediates;
  const P1pa = i.relievingPressureKpaAbs * 1000;
  const out: FormulaBlock[] = [];

  out.push({
    label: "포화액 오메가 파라미터 ωs",
    source: "API 520 Part I 식 (C.30)",
    symbolic: "ωs = 9 × ( ρl1 / ρ9 − 1 )",
    substituted: `= 9 × ( ${n(i.inletLiquidDensityKgM3, 2)} / ${n(i.flashDensityKgM3, 2)} − 1 )`,
    result: n(m.omegaS, 4),
    note: `ρ9 는 포화압 Ps 의 90 % (${n(i.saturationPressureKpaAbs * 0.9, 1)} kPa) 에서 플래시한 밀도`,
  });

  out.push({
    label: "전이 포화압력비 ηst",
    source: "API 520 Part I 식 (C.32)",
    symbolic: "ηst = 2ωs / ( 1 + 2ωs )",
    substituted: `= ( 2 × ${n(m.omegaS, 4)} ) / ( 1 + 2 × ${n(m.omegaS, 4)} )`,
    result: n(m.transitionRatio, 4),
    note:
      `Ps = ${n(i.saturationPressureKpaAbs, 1)} kPa , ηst × P1 = ${n(m.transitionRatio * i.relievingPressureKpaAbs, 1)} kPa` +
      ` → Ps ${r.subcoolingRegion === "high" ? "<" : "≥"} ηst·P1 이므로 **${r.subcoolingRegion === "high" ? "고과냉" : "저과냉"}** 영역 (식 C.31)`,
  });

  out.push(
    r.subcoolingRegion === "high"
      ? {
          label: "임계 압력비 ηc",
          source: "API 520 Part I 식 (C.36)",
          symbolic: "ηc = ηs = Ps / P1        (고과냉 영역)",
          substituted: `= ${n(i.saturationPressureKpaAbs, 1)} / ${n(i.relievingPressureKpaAbs, 1)}`,
          result: n(m.criticalPressureRatio, 4),
          note: `Ps ${i.saturationPressureKpaAbs >= i.backpressureKpaAbs ? "≥" : "<"} P2 (${n(i.backpressureKpaAbs, 1)} kPa) → ${r.flowRegime === "critical" ? "임계유동" : "아임계유동 (전액체 유동)"} (식 C.34)`,
        }
      : {
          label: "임계 압력비 ηc",
          source:
            m.criticalRatioSource === "API 520 Part I (C.37)"
              ? "API 520 Part I 식 (C.37) — 정의식, 수치해"
              : "API 520 Part I 식 (C.38) — 근사식",
          symbolic:
            m.criticalRatioSource === "API 520 Part I (C.37)"
              ? "[ ( ωs + 1/ωs − 2 ) / ( 2ηs ) ] ηc² − 2( ωs − 1 )ηc + ωs·ηs·ln( ηc/ηs ) + (3/2)ωs·ηs − 1 = 0"
              : "ηc = ηs × ( 2ωs / ( 2ωs − 1 ) ) × [ 1 − √( 1 − (1/ηs) × ( ( 2ωs − 1 ) / ( 2ωs ) ) ) ]",
          substituted: `ωs = ${n(m.omegaS, 4)} , ηs = ${n(m.saturationRatio, 4)}`,
          result: n(m.criticalPressureRatio, 4),
          note: `Pcf = ${n(m.criticalPressureRatio * i.relievingPressureKpaAbs, 1)} kPa abs · P2 = ${n(i.backpressureKpaAbs, 1)} kPa abs → ${r.flowRegime === "critical" ? "임계유동" : "아임계유동"} (식 C.33)`,
        },
  );

  out.push(
    r.subcoolingRegion === "high"
      ? {
          label: "질량유속 G",
          source: "API 520 Part I 식 (C.43) — SI, 고과냉",
          symbolic: "G = 1.414 × √( ρl1 × ( P1 − P ) )        (P: 임계면 Ps, 아임계면 P2, 단위 Pa)",
          substituted: `= 1.414 × √( ${n(i.inletLiquidDensityKgM3, 2)} × ( ${ni(P1pa)} − ${ni((r.flowRegime === "critical" ? i.saturationPressureKpaAbs : i.backpressureKpaAbs) * 1000)} ) )`,
          result: `${ni(m.massFlux)} kg/s·m²`,
        }
      : {
          label: "질량유속 G",
          source: "API 520 Part I 식 (C.42) — SI, 저과냉",
          symbolic:
            "G = √( 2( 1 − ηs ) + 2[ ωs·ηs·ln( ηs/η ) − ( ωs − 1 )( ηs − η ) ] ) / ( ωs ( ηs/η − 1 ) + 1 ) × √( P1 × ρl1 )",
          substituted: `ωs = ${n(m.omegaS, 4)} , ηs = ${n(m.saturationRatio, 4)} , η = ${n(r.flowRegime === "critical" ? m.criticalPressureRatio : m.backpressureRatio, 4)} , P1 = ${ni(P1pa)} Pa , ρl1 = ${n(i.inletLiquidDensityKgM3, 2)} kg/m³`,
          result: `${ni(m.massFlux)} kg/s·m²`,
        },
  );

  const kpTerm = m.kp !== null ? ` × ${n(m.kp, 3)}` : "";
  const kpSym = m.kp !== null ? " × Kp" : "";
  out.push({
    label: "소요 유효 오리피스 면적 A",
    source:
      r.equation === "API 520 Part I (C.57)"
        ? "API 520 Part I 식 (C.57) — SI, 비인증 밸브"
        : "API 520 Part I 식 (C.46) — SI, 인증 밸브",
    symbolic:
      r.equation === "API 520 Part I (C.57)"
        ? `A = 16.67 × Q × ρl1 / ( Kd × Kb × Kc × Kv × G${kpSym} ) × √( ( P1 − P2 ) / ( 1.25 × Ps − P2 ) )`
        : "A = 16.67 × Q × ρl1 / ( Kd × Kb × Kc × Kv × G )",
    substituted: `= 16.67 × ${n(i.volumetricFlowLpm, 1)} × ${n(i.inletLiquidDensityKgM3, 2)} / ( ${n(m.kd, 3)} × ${n(m.kb, 3)} × ${n(m.kc, 3)} × ${n(m.kv, 3)} × ${ni(m.massFlux)}${kpTerm} )`,
    result: `${n(r.requiredAreaMm2, 1)} mm²`,
  });
  return out;
}

/** 대기압 상수를 계산서 각주에 쓰기 위해 함께 내보낸다. */
export { ATMOSPHERIC_KPA };
